import { CircularProgress } from '@mui/material';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { getButtonClassName, ButtonStylingProps } from './helper';
import { IndexLinkProps, Link } from '@client/router/compat';
import { sprinkles } from '@design-system/sprinkles.css';
import { Row, RowProps } from '../grid/grid';
import { bgColorsVars } from '@design-system/vars.css';
import { trackSessionEvent } from '@services/session-tracker/sessionTracker';

export type ButtonProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'size'
> &
  ButtonStylingProps & {
    testId?: string;
    'data-testid'?: string;
  } & {
    leftIcon?: IconProp;
    rightIcon?: IconProp;
    loading?: boolean;
    gap?: RowProps['gap'];
  };

export const Button = ({
  leftIcon,
  rightIcon,
  loading,
  testId,
  onClick,
  ...p
}: ButtonProps) => {
  const _onClick: ButtonProps['onClick'] = (e) => {
    if (testId || typeof p.children === 'string')
      trackSessionEvent('ButtonClick', {
        testId,
        content: typeof p.children === 'string' ? p.children : undefined,
      });

    if (onClick) onClick(e);
  };
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={_onClick}
      {...p}
      className={`${getButtonClassName(p)}`}
    >
      <Row gap={p?.gap || 's'} alignCenter className="pointer-events-none">
        {loading ? (
          <div
            className={sprinkles({
              alignItems: 'center',
              display: 'flex',
            })}
          >
            <CircularProgress
              size={16}
              style={{
                color: 'inherit',
              }}
            />
          </div>
        ) : null}
        {leftIcon && <FontAwesomeIcon icon={leftIcon} />}
        {p.children}
        {rightIcon && <FontAwesomeIcon icon={rightIcon} />}
      </Row>
    </button>
  );
};

export type ButtonLinkProps = Omit<IndexLinkProps, 'type' | 'size'> &
  ButtonStylingProps;

export const ButtonLink = (p: ButtonLinkProps) => (
  <Link {...p} className={getButtonClassName(p)}>
    {p.children}
  </Link>
);

export const TextButton = (p: ButtonProps) => (
  <Button variant="text" color="neutral" {...p} />
);

export const AlertButton = (p: ButtonProps) => (
  <TextButton {...p} style={{ color: bgColorsVars['ERROR-100'] }} />
);
